import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';
import axios from 'axios';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Rating,
  Divider,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack,
  Visibility,
  Search,
  Star,
  BugReport,
  Lightbulb,
  TrendingUp,
  Favorite,
  Reply
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface Feedback {
  id: string;
  name: string;
  email: string;
  category: string;
  subject: string;
  message: string;
  rating: number;
  anonymous: boolean;
  createdAt: string;
  status: 'new' | 'read' | 'replied';
  priority: 'low' | 'medium' | 'high';
  deviceInfo?: string;
  stepsToReproduce?: string;
}

const FeedbackManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  // Firebase'den feedback'leri yükle
  useEffect(() => {
    loadFeedbacks();
  }, []);

  const loadFeedbacks = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_BASE_URL}/api/feedbacks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setFeedbacks(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading feedbacks:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'bug': return <BugReport />;
      case 'suggestion': return <Lightbulb />;
      case 'improvement': return <TrendingUp />;
      case 'review': return <Star />;
      case 'story': return <Favorite />;
      default: return <Reply />;
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'bug': return 'Hata';
      case 'suggestion': return 'Öneri';
      case 'improvement': return 'İyileştirme';
      case 'review': return 'Değerlendirme';
      case 'story': return 'Başarı Hikayesi';
      default: return category;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'error';
      case 'read': return 'warning';
      case 'replied': return 'success';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'new': return 'Yeni';
      case 'read': return 'Okundu';
      case 'replied': return 'Yanıtlandı';
      default: return status;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const filteredFeedbacks = feedbacks.filter(feedback => {
    const matchesCategory = filterCategory === 'all' || feedback.category === filterCategory;
    const matchesStatus = filterStatus === 'all' || feedback.status === filterStatus;
    const matchesSearch = searchTerm === '' || 
      feedback.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      feedback.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      feedback.message.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesCategory && matchesStatus && matchesSearch;
  });

  const handleViewDetails = (feedback: Feedback) => {
    setSelectedFeedback(feedback);
    setDetailsOpen(true);
    
    // Mark as read if it's new
    if (feedback.status === 'new') {
      setFeedbacks(prev => prev.map(f => 
        f.id === feedback.id ? { ...f, status: 'read' as const } : f
      ));
    }
  };

  const handleStatusChange = async (feedbackId: string, newStatus: 'new' | 'read' | 'replied') => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.patch(`${API_BASE_URL}/api/feedbacks/${feedbackId}/status`, {
        status: newStatus
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setFeedbacks(prev => prev.map(f =>
          f.id === feedbackId ? { ...f, status: newStatus } : f
        ));
      }
    } catch (error) {
      console.error('Error updating feedback status:', error);
      alert('Durum güncellenirken bir hata oluştu.');
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <AppBar position="static" sx={{ backgroundColor: '#8B4513' }}>
        <Toolbar>
          <IconButton color="inherit" onClick={() => navigate('/admin/dashboard')} size="large">
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, ml: 2 }}>
            Geri Bildirim Yönetimi
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <TextField
              size="small"
              placeholder="Ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ minWidth: 200 }}
            />
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Kategori</InputLabel>
              <Select
                value={filterCategory}
                label="Kategori"
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <MenuItem value="all">Tümü</MenuItem>
                <MenuItem value="bug">Hata</MenuItem>
                <MenuItem value="suggestion">Öneri</MenuItem>
                <MenuItem value="improvement">İyileştirme</MenuItem>
                <MenuItem value="review">Değerlendirme</MenuItem>
                <MenuItem value="story">Başarı Hikayesi</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Durum</InputLabel>
              <Select
                value={filterStatus}
                label="Durum"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">Tümü</MenuItem>
                <MenuItem value="new">Yeni</MenuItem>
                <MenuItem value="read">Okundu</MenuItem>
                <MenuItem value="replied">Yanıtlandı</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>

        {/* Feedback Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tarih</TableCell>
                <TableCell>Gönderen</TableCell>
                <TableCell>Kategori</TableCell>
                <TableCell>Konu</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell>Öncelik</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell>İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Feedback'ler yükleniyor...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredFeedbacks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Henüz feedback bulunmuyor.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredFeedbacks.map((feedback) => (
                <TableRow key={feedback.id} hover>
                  <TableCell>
                    {new Date(feedback.createdAt).toLocaleDateString('tr-TR')}
                  </TableCell>
                  <TableCell>
                    {feedback.anonymous ? 'Anonim' : feedback.name}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getCategoryIcon(feedback.category)}
                      {getCategoryLabel(feedback.category)}
                    </Box>
                  </TableCell>
                  <TableCell>{feedback.subject}</TableCell>
                  <TableCell>
                    <Chip 
                      label={getStatusLabel(feedback.status)} 
                      color={getStatusColor(feedback.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={feedback.priority.toUpperCase()} 
                      color={getPriorityColor(feedback.priority) as any}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {feedback.rating > 0 && (
                      <Rating value={feedback.rating} readOnly size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      startIcon={<Visibility />}
                      onClick={() => handleViewDetails(feedback)}
                    >
                      Görüntüle
                    </Button>
                  </TableCell>
                </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>

      {/* Details Dialog */}
      <Dialog 
        open={detailsOpen} 
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedFeedback && (
          <>
            <DialogTitle>
              <Box display="flex" alignItems="center" gap={2}>
                {getCategoryIcon(selectedFeedback.category)}
                <div>
                  <Typography variant="h6">
                    {selectedFeedback.subject}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedFeedback.anonymous ? 'Anonim' : selectedFeedback.name} • {' '}
                    {new Date(selectedFeedback.createdAt).toLocaleString('tr-TR')}
                  </Typography>
                </div>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box mb={2}>
                <Typography variant="body1" paragraph>
                  {selectedFeedback.message}
                </Typography>
              </Box>

              {selectedFeedback.rating > 0 && (
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Değerlendirme:
                  </Typography>
                  <Rating value={selectedFeedback.rating} readOnly />
                </Box>
              )}

              {selectedFeedback.deviceInfo && (
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Cihaz Bilgisi:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedFeedback.deviceInfo}
                  </Typography>
                </Box>
              )}

              {selectedFeedback.stepsToReproduce && (
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Tekrarlama Adımları:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" style={{ whiteSpace: 'pre-line' }}>
                    {selectedFeedback.stepsToReproduce}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Box display="flex" gap={1} flexWrap="wrap">
                <Chip 
                  label={getCategoryLabel(selectedFeedback.category)} 
                  icon={getCategoryIcon(selectedFeedback.category)}
                  size="small"
                />
                <Chip 
                  label={getStatusLabel(selectedFeedback.status)} 
                  color={getStatusColor(selectedFeedback.status) as any}
                  size="small"
                />
                <Chip 
                  label={`${selectedFeedback.priority.toUpperCase()} ÖNCELİK`} 
                  color={getPriorityColor(selectedFeedback.priority) as any}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailsOpen(false)}>
                Kapat
              </Button>
              <Button 
                variant="contained" 
                color="primary"
                onClick={() => {
                  handleStatusChange(selectedFeedback.id, 'replied');
                  setDetailsOpen(false);
                }}
                disabled={selectedFeedback.status === 'replied'}
              >
                Yanıtlandı Olarak İşaretle
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </div>
  );
};

export default FeedbackManagementPage;
